import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { devNoteService } from "../../services/devNoteService";
import { useToast } from "../../context/ToastContext";
import Icon from "../../components/Common/Icon";
import DevNoteModal from "../../components/Dev/DevNoteModal";
import ConfirmationModal from "../../components/Common/ConfirmationModal";

const STATUS_CLASSES = {
  KARARLI: "bg-success/10 text-success border-success/20",
  PLANLANAN: "bg-primary/10 text-primary border-primary/20",
};

export default function DevVersionsPage() {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.role === "ADMIN";
  const { showToast } = useToast();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  // Confirm Modal State
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await devNoteService.listByType("VERSION");
      setNotes(res.data.data ?? []);
    } catch (err) {
      showToast("Versiyon notları yüklenemedi.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateClick = () => {
    setSelectedNote(null);
    setModalOpen(true);
  };

  const handleEditClick = (note) => {
    setSelectedNote(note);
    setModalOpen(true);
  };

  const handleDeleteClick = (note) => {
    setConfirmModal({
      isOpen: true,
      title: "Versiyon Notunu Sil",
      message: `"${note.title}" başlıklı değişikliği silmek istediğinize emin misiniz?`,
      onConfirm: async () => {
        try {
          await devNoteService.delete(note.id);
          showToast("Versiyon notu başarıyla silindi.", "success");
          fetchNotes();
        } catch (err) {
          showToast("Not silinemedi.", "error");
        } finally {
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleModalSubmit = async (data) => {
    try {
      if (selectedNote) {
        await devNoteService.update(selectedNote.id, data);
        showToast("Versiyon notu başarıyla güncellendi.", "success");
      } else {
        await devNoteService.create(data);
        showToast("Yeni versiyon notu başarıyla oluşturuldu.", "success");
      }
      setModalOpen(false);
      fetchNotes();
    } catch (err) {
      showToast("İşlem gerçekleştirilemedi.", "error");
    }
  };

  // Group notes by version for timeline display
  const groupVersions = (notesList) => {
    const map = {};
    notesList.forEach((note) => {
      const v = note.version || "Diğer";
      if (!map[v]) {
        map[v] = {
          version: v,
          date: note.releaseDate || "—",
          status: note.status || "PLANLANAN",
          changes: [],
        };
      }
      map[v].changes.push(note);
    });
    return Object.values(map);
  };

  const grouped = groupVersions(notes);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="history" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Versiyon Notları</h1>
            <p className="text-on-surface-variant text-sm">
              Sisteme eklenen son güncellemeler, yeni özellikler ve versiyon geçmişi.
            </p>
          </div>
        </div>

        {isAdmin && (
          <button
            onClick={handleCreateClick}
            className="bg-primary text-white px-4 py-2 rounded-xl text-xs font-semibold hover:opacity-90 transition flex items-center gap-1.5 self-start sm:self-auto shadow-sm"
          >
            <Icon name="add" className="!text-[16px]" /> Yeni Ekle
          </button>
        )}
      </div>

      {loading ? (
        <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
          <p>Notlar yükleniyor…</p>
        </div>
      ) : notes.length === 0 ? (
        <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
          <p>Henüz eklenmiş bir versiyon notu bulunmuyor.</p>
        </div>
      ) : (
        <div className="relative border-l-2 border-outline-variant/60 ml-4 pl-8 space-y-12 py-4">
          {grouped.map((v) => (
            <div key={v.version} className="relative animate-slide-in">
              {/* Timeline Dot */}
              <span className="absolute -left-[41px] top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white border-2 border-primary">
                <span className="h-2 w-2 rounded-full bg-primary" />
              </span>

              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-bold text-on-surface">{v.version}</h3>
                  <span className="text-xs text-on-surface-variant font-medium">{v.date}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                      STATUS_CLASSES[v.status] ?? "bg-surface-container border-outline-variant"
                    }`}
                  >
                    {v.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {v.changes.map((c) => (
                    <div
                      key={c.id}
                      className="bg-white p-5 rounded-2xl border border-outline-variant/40 shadow-sm flex flex-col justify-between hover:border-primary/20 transition-all group"
                    >
                      <div className="space-y-2">
                        <div className="flex justify-between items-start gap-4">
                          <h4 className="font-bold text-sm text-on-surface flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                            {c.title}
                          </h4>
                          {isAdmin && (
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleEditClick(c)}
                                className="p-1 rounded text-primary hover:bg-primary/10 transition"
                                title="Düzenle"
                              >
                                <Icon name="edit" className="!text-[14px]" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(c)}
                                className="p-1 rounded text-error hover:bg-error/10 transition"
                                title="Sil"
                              >
                                <Icon name="delete" className="!text-[14px]" />
                              </button>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-on-surface-variant leading-relaxed">
                          {c.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* DevNote Add/Edit Modal */}
      <DevNoteModal
        isOpen={modalOpen}
        initialData={selectedNote}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type="danger"
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
